import { useState, useRef, useCallback, useEffect } from 'react';
import { useScribe, CommitStrategy } from '@elevenlabs/react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { triggerHaptic } from './useHaptics';

export type VoiceInputState = 'idle' | 'recording' | 'transcribing';

export function useVoiceInput(onTranscript: (text: string) => void) {
  const [state, setState] = useState<VoiceInputState>('idle');
  const [liveText, setLiveText] = useState('');
  const finalTextRef = useRef('');

  const scribe = useScribe({
    modelId: 'scribe_v2_realtime',
    commitStrategy: CommitStrategy.VAD, // Voice Activity Detection - auto-commits on silence
    onPartialTranscript: (data) => {
      setLiveText(finalTextRef.current + (finalTextRef.current ? ' ' : '') + data.text);
    },
    onCommittedTranscript: (data) => {
      finalTextRef.current = finalTextRef.current + (finalTextRef.current ? ' ' : '') + data.text;
      setLiveText(finalTextRef.current);
    },
  });

  // Sync state with scribe connection status
  useEffect(() => {
    if (scribe.isConnected) {
      setState('recording');
    }
  }, [scribe.isConnected]);

  const startRecording = useCallback(async () => {
    try {
      setState('transcribing'); // Brief loading state while getting token
      setLiveText('');
      finalTextRef.current = '';

      // Get token from edge function
      const { data, error } = await supabase.functions.invoke('scribe-token');

      if (error || !data?.token) {
        console.error('Failed to get scribe token:', error || data);
        toast.error('Could not start voice input', {
          description: 'Please try again'
        });
        setState('idle');
        return;
      }

      // Start the scribe session with microphone
      await scribe.connect({
        token: data.token,
        microphone: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      triggerHaptic('medium');
    } catch (error) {
      console.error('Voice input error:', error);
      
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          toast.error('Microphone access denied', {
            description: 'Please allow microphone access in your browser settings'
          });
        } else if (error.name === 'NotFoundError') {
          toast.error('No microphone found', {
            description: 'Please connect a microphone and try again'
          });
        } else {
          toast.error('Microphone error', {
            description: error.message
          });
        }
      } else {
        toast.error('Could not start voice input');
      }
      
      setState('idle');
    }
  }, [scribe]);

  const stopRecording = useCallback(async () => {
    try {
      scribe.disconnect();
      
      // Use the accumulated text
      const finalText = finalTextRef.current.trim() || liveText.trim();
      
      if (finalText) {
        onTranscript(finalText);
        triggerHaptic('success');
      } else {
        toast.info('No speech detected', {
          description: 'Please speak clearly and try again'
        });
      }
      
      setLiveText('');
      finalTextRef.current = '';
      setState('idle');
      triggerHaptic('light');
    } catch (error) {
      console.error('Error stopping recording:', error);
      setState('idle');
    }
  }, [scribe, liveText, onTranscript]);

  const toggleRecording = useCallback(async () => {
    if (state === 'recording' || scribe.isConnected) {
      await stopRecording();
    } else if (state === 'idle') {
      await startRecording();
    }
    // If transcribing (connecting), do nothing
  }, [state, scribe.isConnected, startRecording, stopRecording]);

  const cancelRecording = useCallback(() => {
    scribe.disconnect();
    setLiveText('');
    finalTextRef.current = '';
    setState('idle');
    triggerHaptic('light');
  }, [scribe]);

  return {
    state,
    isRecording: state === 'recording' || scribe.isConnected,
    isTranscribing: state === 'transcribing' && !scribe.isConnected,
    liveText,
    toggleRecording,
    cancelRecording,
  };
}
